import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { MessageBoxService } from '@core/service/message-box.service';
import { UserService } from '@workbench/user-2/user.service';

@Component({
    selector: 'app-reset-password',
    templateUrl: 'reset-password.component.html',
    styleUrls: ['reset-password.component.scss'],
})
export class ResetPasswordComponent implements OnInit {
    private _token: string = this._activatedRoute.snapshot.queryParams.token;

    public data!: any;
    public isFail!: boolean;
    public isError!: boolean;
    public loading!: boolean;
    public isSuccess!: boolean;
    public formGroup!: FormGroup<any>;
    public submitted!: boolean;
    public resendLink!: boolean;
    public isSubmitted!: boolean;
    public isValidPassword!: boolean;
    public loadResetPassword: boolean = false;

    constructor(
        private readonly _title: Title,
        private readonly _formBuilder: FormBuilder,
        private readonly _userService: UserService,
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _messageBoxService: MessageBoxService,
        private readonly _changeDetectorRef: ChangeDetectorRef
    ) {}

    private _matchValidator(
        controlName: string,
        matchingControlName: string
    ): ValidatorFn {
        return (abstractControl: AbstractControl) => {
            const control: any = abstractControl.get(controlName);
            const matchingControl: any =
                abstractControl.get(matchingControlName);

            if (
                matchingControl!.errors &&
                !matchingControl!.errors?.['confirmedValidator']
            ) {
                return null;
            }

            if (control!.value !== matchingControl!.value) {
                const error = {
                    confirmedValidator: 'Password confirmation does not match',
                };

                matchingControl!.setErrors(error);

                return error;
            } else {
                matchingControl!.setErrors(null);

                return null;
            }
        };
    }

    private _setupForm(): void {
        const ctrl: any = this._formBuilder.control.bind(this);

        this.formGroup = this._formBuilder.group<any>(
            {
                password: ctrl(null, [Validators.required, Validators.min(8)]),
                confirmPassword: ctrl(null, [Validators.required]),
            },
            {
                validators: this._matchValidator('password', 'confirmPassword'),
            }
        );
    }

    public handleChangeInputPassword(): void {
        this.submitted = false;
    }

    public handleResendVerificationToken(): void {
        this._userService.resendVerificationToken(this._token).subscribe(
            () => (this.resendLink = true),
            () => this._messageBoxService.showError('test', 'test')
        );
    }

    public handleResetPassword(): void {
        this.submitted = true;
        this.loadResetPassword = true;

        if (this.formGroup.invalid) {
            return this.formGroup.markAllAsTouched();
        } else {
            if (!this.isSubmitted) {
                this.isSubmitted = true;
            }

            this.isFail = this.submitted && !this.isValidPassword;
        }

        if (!this.isFail) {
            this._userService
                .resetPassword({
                    token: this._token,
                    password: this.formGroup.get('password').value,
                })
                .subscribe(() => {
                    this.isSuccess = true;
                    this.loadResetPassword = false;

                    setTimeout(() => {
                        window.location.href = `${window.origin}/login`;
                    }, 2500);
                });
        } else {
            this.loadResetPassword = false;
        }
    }

    public handleValidPasswordInfo(value: boolean): void {
        this.isValidPassword = value;

        if (this.isValidPassword) {
            this.isFail = false;
        }

        this._changeDetectorRef.detectChanges();
    }

    public verifyEmail(): void {
        this.loading = true;

        this._userService.verifyResetToken(this._token).subscribe(
            (res: any) => {
                this.data = res.data;

                this._setupForm();
            },
            (err: any) => {
                console.error(err);
                this.isError = true;
                this.loading = false;
            }
        );
    }

    public ngOnInit(): void {
        this._title.setTitle('Reset Password | LinovHR');

        this.verifyEmail();
    }
}
